export class ProductQueryParams {
  public email: string;
  public channel: string;
  public locale: string;
  public filters: Filters;
  public pagination: Pagination;
  public searchKeyWord: string;
  public shopName?: string;

  constructor() {
    this.email = ''; // NOSONAR
    this.channel = null;
    this.locale = null;
    this.shopName = null;
    this.filters = {
      categories: [],
      brands: [],
      statusFormat: [],
      editorialCharter: [],
      publicationStatus: [],
      fromDate: null,
      toDate: null
    };
    this.pagination = {
      pageSize: 100,
      offset: 0,
      orderColumn: '',
      orderDirection: '',
    };
    this.searchKeyWord = '';
  }
  addParams(productQueryParams, searchFilters) {
    productQueryParams.filters.editorialCharter = searchFilters['editorialCharter'] ?? [];
    productQueryParams.filters.publicationStatus = searchFilters['publicationStatus'] ?? [];
    productQueryParams.filters.statusFormat = searchFilters['statusFormat'] ?? [];

    productQueryParams.filters.fromDate = searchFilters['lastImportDate'] ? searchFilters['lastImportDate'][0] : null;
    productQueryParams.filters.toDate = searchFilters['lastImportDate'] ? searchFilters['lastImportDate'][1] : null;

    productQueryParams.filters.categories = searchFilters['category'] ?? [];
    productQueryParams.filters.brands = searchFilters['brands'] ?? [];
    productQueryParams.filters.sellers = searchFilters['shopMultiple'] ?? [];

    return productQueryParams;
  }

  get() {
    return {
      email: this.email,
      channel: this.channel,
      locale: this.locale,
      shopName: this.shopName,
      filters: this.filters,
      pagination: this.pagination,
      searchKeyWord: this.searchKeyWord,
    }
  }

  getExport() {
    return {
      email: this.email,
      channel: this.channel,
      shopName: this.shopName,
      filters: this.filters,
      searchKeyWord: this.searchKeyWord,
    }
  }
}

import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";


export interface ProductQueryParamsData {
  email: string;
  channel: string;
  locale: string;
  filters: Filters;
  pagination: Pagination;
  searchKeyWord: string;
  shopName?: string;
  tags?: any;
  appliedFilters?: any;
  shopCode?: string;
}

@Injectable({ providedIn: 'root' })
export class ProductQueryService {
  private readonly initialData: ProductQueryParamsData = {
    email: '',
    channel: null,
    locale: null,
    shopName: null,
    shopCode: null,
    filters: {
      categories: [],
      brands: [],
      statusFormat: [],
      editorialCharter: [],
      publicationStatus: [],
      fromDate: null,
      toDate: null,
      sellers: [],
    },
    tags: {},
    appliedFilters: {},
    pagination: {
      pageSize: 100,
      offset: 0,
      orderColumn: '',
      orderDirection: '',
    },
    searchKeyWord: '',
  };
  private readonly _queryParams = new BehaviorSubject<ProductQueryParamsData>(
    this.initialData,
  );

  getQueryParams(): Observable<ProductQueryParamsData> {
    return this._queryParams.asObservable();
  }
  getCurrentValue(): ProductQueryParamsData {
    return this._queryParams.value;
  }

  private updateField<K extends keyof ProductQueryParamsData>(
    field: K,
    value: ProductQueryParamsData[K],
  ) {
    this._queryParams.next({ ...this._queryParams.value, [field]: value });
  }

  get email() {
    return this._queryParams.value.email;
  }
  set email(v: string) {
    this.updateField('email', v);
  }

  get channel() {
    return this._queryParams.value.channel;
  }
  set channel(v: string) {
    this.updateField('channel', v);
  }

  get locale() {
    return this._queryParams.value.locale;
  }
  set locale(v: string) {
    this.updateField('locale', v);
  }

  get shopName() {
    return this._queryParams.value.shopName;
  }
  set shopName(v: string) {
    this.updateField('shopName', v);
  }

  get shopCode() {
    return this._queryParams.value.shopCode;
  }
  set shopCode(v: string) {
    this.updateField('shopCode', v);
  }

  get searchKeyWord() {
    return this._queryParams.value.searchKeyWord;
  }
  set searchKeyWord(v: string) {
    this.updateField('searchKeyWord', v);
  }

  get filters() {
    return this._queryParams.value.filters;
  }
  set filters(v: Filters) {
    this.updateField('filters', v);
  }

  get tags() {
    return this._queryParams.value.tags;
  }
  set tags(v: any) {
    this.updateField('tags', v);
  }
  get appliedFilters() {
    return this._queryParams.value.appliedFilters;
  }
  set appliedFilters(v: any) {
    this.updateField('appliedFilters', v);
  }

  get pagination() {
    return this._queryParams.value.pagination;
  }
  set pagination(v: Pagination) {
    this.updateField('pagination', v);
  }

  addParams(searchFilters: any): void {
    const f = { ...this.filters };
    f.editorialCharter = searchFilters.editorialCharter ?? [];
    f.publicationStatus = searchFilters.publicationStatus ?? [];
    f.statusFormat = searchFilters.statusFormat ?? [];
    f.fromDate = searchFilters.lastImportDate?.[0] ?? searchFilters.fromDate ?? null;
    f.toDate = searchFilters.lastImportDate?.[1] ?? searchFilters.toDate ?? null;
    f.categories = searchFilters.category ?? [];
    f.brands = searchFilters.brands ?? [];
    f.sellers = (searchFilters.shopMultiple || searchFilters.sellers) ?? [];
    this.filters = f;
  }

  resetFilters(): void {
    const { pagination, ...rest } = this._queryParams.value;
    this._queryParams.next({
      ...rest,
      searchKeyWord: '',
      filters: {
        categories: [],
        brands: [],
        statusFormat: [],
        editorialCharter: [],
        publicationStatus: [],
        fromDate: null,
        toDate: null,
        sellers: [],
      },
      pagination: { ...pagination, offset: 0 },
    });
  }

  resetAll(): void {
    this._queryParams.next({ ...this.initialData });
  }

  hasFilters(): boolean {
    const f = this.filters;
    return !!(
      f.categories.length ||
      f.brands.length ||
      f.statusFormat.length ||
      f.editorialCharter.length ||
      f.publicationStatus.length ||
      f.sellers.length ||
      f.fromDate ||
      f.toDate ||
      this.searchKeyWord.trim()
    );
  }

  getExport() {
    return {
      email: this.email,
      channel: this.channel,
      shopName: this.shopName,
      filters: this.filters,
      searchKeyWord: this.searchKeyWord,
    };
  }

  get() {
    return {
      email: this.email,
      channel: this.channel,
      locale: this.locale,
      shopName: this.shopName,
      filters: this.filters,
      pagination: this.pagination,
      searchKeyWord: this.searchKeyWord,
      shopCode: this.shopCode
    };
  }
}

export interface Filters {
  categories: string[];
  brands: string[];
  sellers?: string[];
  statusFormat: string[];
  editorialCharter: string[];
  publicationStatus: string[];
  fromDate: string;
  toDate: string;
}

export interface Pagination {
  pageSize: number;
  offset: number;
  orderColumn: any;
  orderDirection: string;
}
